import { useMemo } from 'react';
import { useIntl, useSelector } from 'umi';
import { LANGUAGE_CN } from '@intelligence/_config/config';
import { CONFIGURE_TEXTS_MODEL_NAMESPACE, DEFAULT_CONFIGURE_TEXTS_STATE } from '@intelligence/models/configureTexts';

import type { IConfigureTextsState } from '@intelligence/models/configureTexts';

interface IFormatConfigureTextBaseParams {
  locale?: string;
  configureTextsState?: IConfigureTextsState | null;
}

interface IFormatConfigureTextParams extends IFormatConfigureTextBaseParams {
  key?: string;
}

const formatConfigureText = (params?: IFormatConfigureTextParams): string => {
  const { locale, key, configureTextsState } = params || {};
  if (!key) return '';
  if (!!configureTextsState?.loading) return '';
  const { en: configureTextsEN, cn: configureTextsCN } = configureTextsState?.data || {};
  const configureTexts = locale === LANGUAGE_CN ? configureTextsCN : configureTextsEN;
  const defaultConfigureText = configureTextsEN?.[key] || configureTextsCN?.[key] || '';
  return configureTexts?.[key] || defaultConfigureText;
};

export default function useConfigureTexts() {
  const { locale } = useIntl();

  const configureTextsState = useSelector<any, IConfigureTextsState>((rootState) => {
    return rootState?.[CONFIGURE_TEXTS_MODEL_NAMESPACE] || DEFAULT_CONFIGURE_TEXTS_STATE;
  });

  const configureTexts = useMemo<Record<string, string>>(() => {
    return {
      // 国家筛选器右侧global解释文案（范围参照tapd单：https://tapd.tencent.com/tapd_fe/20427644/story/detail/1020427644123793502?from_iteration_id=1020427644002140189）
      global: formatConfigureText({ locale, configureTextsState, key: 'select_region_global_desc' }),
      // 国家筛选器右侧global_wo_china解释文案（范围参照tapd单：https://tapd.tencent.com/tapd_fe/20427644/story/detail/1020427644123793502?from_iteration_id=1020427644002140189）
      global_wo_china: formatConfigureText({ locale, configureTextsState, key: 'select_region_global_wo_china_desc' }),
      // 产品分类选择器的提示内容（产品分类筛选器，涉及页面Compare/Multi-dimension、PCConsole详情页KPI Trends页面）
      segment_for_sales_select_tips: formatConfigureText({
        locale,
        configureTextsState,
        key: 'select_segment_for_sales_desc',
      }),
      // PCConsole详情页KPI Overview页签Steam Data模块标题右侧提示文案
      pcconsole_details_kpi_overview_steam_data_card_title_tips: formatConfigureText({
        locale,
        configureTextsState,
        key: 'tips_pcconsole_details_kpi_overview_steam_data_card_title',
      }),
      // 数据隐私（弹窗内容）
      intelligence_data_privacy_content: formatConfigureText({
        locale,
        configureTextsState,
        key: 'intelligence_data_privacy_content',
      }),
      // CasualCompetitors 品类Tab提示文案
      home_studio_casual_competitors_genre_type_tips_general_casual: formatConfigureText({
        locale,
        configureTextsState,
        key: 'home_studio_casual_competitors_genre_type_tips_general_casual',
      }),
      home_studio_casual_competitors_genre_type_tips_iaa_mahjong: formatConfigureText({
        locale,
        configureTextsState,
        key: 'home_studio_casual_competitors_genre_type_tips_iaa_mahjong',
      }),
      home_studio_casual_competitors_genre_type_tips_iaa_arrows: formatConfigureText({
        locale,
        configureTextsState,
        key: 'home_studio_casual_competitors_genre_type_tips_iaa_arrows',
      }),
      home_studio_casual_competitors_genre_type_tips_iaa_all: formatConfigureText({
        locale,
        configureTextsState,
        key: 'home_studio_casual_competitors_genre_type_tips_iaa_all',
      }),
      home_studio_casual_competitors_genre_type_tips_iaa: formatConfigureText({
        locale,
        configureTextsState,
        key: 'home_studio_casual_competitors_genre_type_tips_iaa',
      }),
      home_studio_casual_competitors_genre_type_tips_match3: formatConfigureText({
        locale,
        configureTextsState,
        key: 'home_studio_casual_competitors_genre_type_tips_match3',
      }),
      home_studio_casual_competitors_genre_type_tips_merge2: formatConfigureText({
        locale,
        configureTextsState,
        key: 'home_studio_casual_competitors_genre_type_tips_merge2',
      }),
      home_studio_casual_competitors_genre_type_tips_lucky_battle: formatConfigureText({
        locale,
        configureTextsState,
        key: 'home_studio_casual_competitors_genre_type_tips_lucky_battle',
      }),
      home_studio_casual_competitors_genre_type_tips_hybrid_casual: formatConfigureText({
        locale,
        configureTextsState,
        key: 'home_studio_casual_competitors_genre_type_tips_hybrid_casual',
      }),
      home_studio_casual_competitors_genre_type_tips_simulation: formatConfigureText({
        locale,
        configureTextsState,
        key: 'home_studio_casual_competitors_genre_type_tips_simulation',
      }),
      home_studio_casual_competitors_genre_type_tips_rpgtd: formatConfigureText({
        locale,
        configureTextsState,
        key: 'home_studio_casual_competitors_genre_type_tips_rpgtd',
      }),
    };
  }, [locale, configureTextsState]);

  return { loading: !!configureTextsState?.loading, configureTexts };
}
